import { TuiBadge, TuiChip, TuiSkeleton } from '@taiga-ui/kit';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import {
  AsyncPipe,
  KeyValuePipe,
  NgClass,
  NgTemplateOutlet,
} from '@angular/common';
import { TuiExpand, TuiIcon, TuiButton, TuiHint } from '@taiga-ui/core';
import { RequestProduct } from '@core/types/RequestProduct';
import { IsLoadingService } from '@core/services';
import { OutkeepPermissionService } from '@core/services/permission/outkeep-permission.service';
import { RequestProductRequiredFieldsService } from '@core/services/request/request-product-required-fields.service';
import { BehaviorSubject, Observable, switchMap, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounce } from '@core/helpers';
import { RequestProductAndGoodsService } from '@core/services/request/request-product-and-goods.service';
import { OutkeepTranslateService } from '@core/services/outkeep-translate.service';
import {
  RequestFormGoodsTranslations,
  RequestFormGoodsTranslationsPayload,
} from '@core/types';
import { TranslatePipe } from '@ngx-translate/core';
import { EntityTranslatePipe } from '@core/pipes';
import { RequestProductIconComponent } from '@features/requests/request-product-icon/request-product-icon.component';
import { RequestProductIconSize } from '@features/requests/request-product-icon/types';
import { RequestModel } from '@core/models';

@Component({
  selector: 'x-request-form-goods-item',
  imports: [
    TuiSkeleton,
    TuiIcon,
    TuiExpand,
    TuiBadge,
    KeyValuePipe,
    TuiButton,
    NgClass,
    AsyncPipe,
    TuiHint,
    NgTemplateOutlet,
    TuiChip,
    TranslatePipe,
    EntityTranslatePipe,
    RequestProductIconComponent,
  ],
  templateUrl: './request-form-goods-item.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestFormGoodsItemComponent implements OnInit {
  readonly $requestProduct = input.required<RequestProduct>({
    alias: 'requestProduct',
  });

  readonly $requestDevices = input.required<RequestModel['devices']>({
    alias: 'requestDevices',
  });

  readonly $expanded = input<boolean>(false, {
    alias: 'expanded',
  });

  readonly $isInsideSummary = input<boolean>(false, {
    alias: 'isInsideSummary',
  });

  expandOutputEvent = output<RequestProduct['id']>();

  readonly isLoading$: BehaviorSubject<boolean> = inject(IsLoadingService);
  private readonly outkeepTranslateService = inject(OutkeepTranslateService);

  private translates: RequestFormGoodsTranslationsPayload = {
    [RequestFormGoodsTranslations.ErrorsAfterChecking]:
      'Имеются следующие ошибки: ',
    [RequestFormGoodsTranslations.SuccessAfterChecking]: 'Все поля заполнены',
  };

  public isInitialLoad = true;
  readonly permissionService = inject(OutkeepPermissionService);
  private readonly requestProductRequiredFieldsService = inject(
    RequestProductRequiredFieldsService,
  );
  private readonly requestProductAndGoodsService = inject(
    RequestProductAndGoodsService,
  );
  private readonly destroyRef = inject(DestroyRef);
  public $productSettingsError = signal<string>('');
  public $productSettingsAllCompleted = signal<string>('');
  public $isCheckingRequiredFields = signal<boolean>(false);
  protected readonly isArray = Array.isArray;

  readonly $requiredSettingsForProducts =
    this.requestProductRequiredFieldsService.$entities;

  constructor() {
    const debouncedPerformRequiredFieldsChecking = debounce(
      this.performRequiredFieldsChecking.bind(this),
      300,
    );

    effect(() => {
      if (
        this.$expanded() &&
        !this.$requestProduct().completed &&
        !this.$isInsideSummary()
      ) {
        debouncedPerformRequiredFieldsChecking();
      }
    });

    effect(() => {
      if (this.$isInsideSummary()) {
        setTimeout(
          debouncedPerformRequiredFieldsChecking.bind(this),
          Math.floor(Math.random() * (300 - 100 + 1)) + 100,
        );
      }
    });
  }

  public ngOnInit(): void {
    this.outkeepTranslateService
      .translate(Object.values(RequestFormGoodsTranslations))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(translates => (this.translates = translates));

    this.outkeepTranslateService
      .onLangChange()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.performRequiredFieldsChecking();
      });

    this.requestProductRequiredFieldsService.entities$
      .pipe(
        tap(() => {
          this.isInitialLoad = false;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  public toggleExpand(event: Event, id: RequestProduct['id']): void {
    this.expandOutputEvent.emit(id);
  }

  public toggleCompleteProduct(event: Event): void {
    this.handleEvent(event, () =>
      this.requestProductAndGoodsService.performToggleRequestProductCompletion$(
        this.$requestProduct(),
      ),
    );
  }

  public closeProductForcibly(event: Event): void {
    const requestProduct = this.$requestProduct();

    this.handleEvent(event, () =>
      this.requestProductRequiredFieldsService
        .performRequiredFieldsChecking$(requestProduct)
        .pipe(
          switchMap(result => {
            if (!result.isSuccess) {
              return this.requestProductAndGoodsService.showCompleteForciblyModalForm(
                requestProduct,
                result.helpText,
              );
            }

            return this.requestProductAndGoodsService.performToggleRequestProductCompletion$(
              requestProduct,
            );
          }),
          takeUntilDestroyed(this.destroyRef),
        ),
    );
  }

  public cancelForciblyCompletion(event: Event): void {
    this.handleEvent(event, () =>
      this.requestProductAndGoodsService.cancelForciblyCompletion(
        this.$requestProduct(),
      ),
    );
  }

  private handleEvent(event: Event, action: () => Observable<any>): void {
    event.stopPropagation();
    const requestProduct = this.$requestProduct();

    if (!requestProduct) {
      return;
    }
    action()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(res => {
        if (
          res?.completed &&
          res?.isClosedForcingly === false &&
          this.$productSettingsError()
        ) {
          this.$productSettingsError.set('');
        }
      });
  }

  public performRequiredFieldsChecking(): void {
    if (this.$isCheckingRequiredFields()) {
      return;
    }

    this.$isCheckingRequiredFields.set(true);

    this.requestProductRequiredFieldsService
      .performRequiredFieldsChecking$(this.$requestProduct())
      .pipe(
        tap(result => {
          if (!result.isSuccess) {
            this.$productSettingsError.set(
              result.helpText?.split(
                this.translates[
                  RequestFormGoodsTranslations.ErrorsAfterChecking
                ],
              )[1],
            );
          } else {
            if (this.$productSettingsError()) {
              this.$productSettingsError.set('');
            }

            this.$productSettingsAllCompleted.set(
              this.translates[
                RequestFormGoodsTranslations.SuccessAfterChecking
              ],
            );
          }

          this.$isCheckingRequiredFields.set(false);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        error: () => {
          this.$isCheckingRequiredFields.set(false);
        },
      });
  }

  protected readonly RequestProductIconSize = RequestProductIconSize;
}
